import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { mockExamQuestions, MockExamQuestion } from '../mock-exam/data/mockExamQuestions';
import DomainPriorityCard from './components/DomainPriorityCard';
import WeeklySchedule from './components/WeeklySchedule';

// ── Types ──────────────────────────────────────────────────────────────────
type Domain = MockExamQuestion['domain'];

interface DomainScore {
  domain: Domain;
  correct: number;
  total: number;
  pct: number;
  priority: 'critical' | 'needs-work' | 'good' | 'strong';
  subtopics: { name: string; correct: number; total: number }[];
}

// ── Domain meta ────────────────────────────────────────────────────────────
const DOMAIN_META: Record<Domain, {
  gradient: string;
  bg: string;
  text: string;
  border: string;
  badge: string;
  icon: string;
  practiceLink: string;
  tips: string[];
  weeklyGoals: Record<'critical' | 'needs-work' | 'good' | 'strong', string>;
}> = {
  'Algebra': {
    gradient: 'from-emerald-500 to-emerald-600',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    icon: 'ri-function-line',
    practiceLink: '/sat-prep/algebra-practice',
    tips: [
      'Always isolate the variable step-by-step — show every line of work.',
      'For systems, try elimination first; substitution for one-variable systems.',
      'Memorize slope-intercept form and point-slope form cold.',
      'When graphing inequalities, test a point to confirm the shaded region.',
    ],
    weeklyGoals: {
      critical: '5 sessions × 45 min — drill linear equations & systems daily',
      'needs-work': '3 sessions × 30 min — focus on inequalities & functions',
      good: '2 sessions × 20 min — maintain with mixed review',
      strong: '1 session × 15 min — light review to stay sharp',
    },
  },
  'Advanced Math': {
    gradient: 'from-teal-500 to-teal-600',
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    border: 'border-teal-200',
    badge: 'bg-teal-100 text-teal-700',
    icon: 'ri-superscript-2',
    practiceLink: '/sat-prep/advanced-math-practice',
    tips: [
      'Factor before using the quadratic formula — factoring is faster when possible.',
      'Memorize difference of squares and difference/sum of cubes patterns.',
      'For exponential equations, rewrite both sides with the same base.',
      'Always check radical equation solutions by substituting back in.',
    ],
    weeklyGoals: {
      critical: '5 sessions × 45 min — quadratics, polynomials & functions daily',
      'needs-work': '3 sessions × 35 min — focus on quadratic formula & exponentials',
      good: '2 sessions × 25 min — mixed advanced practice sets',
      strong: '1 session × 15 min — challenge problems to maintain mastery',
    },
  },
  'Problem-Solving & Data Analysis': {
    gradient: 'from-cyan-500 to-cyan-600',
    bg: 'bg-cyan-50',
    text: 'text-cyan-700',
    border: 'border-cyan-200',
    badge: 'bg-cyan-100 text-cyan-700',
    icon: 'ri-bar-chart-line',
    practiceLink: '/sat-prep/problem-solving-practice',
    tips: [
      'For percent change, always divide by the ORIGINAL value, not the new one.',
      'Sort data before finding median — never skip this step.',
      'Use the complement rule for probability: P(not A) = 1 − P(A).',
      'Read scatter plot axes carefully — units matter for predictions.',
    ],
    weeklyGoals: {
      critical: '5 sessions × 40 min — ratios, stats & probability every day',
      'needs-work': '3 sessions × 30 min — focus on data interpretation & percent problems',
      good: '2 sessions × 20 min — mixed data analysis practice',
      strong: '1 session × 15 min — quick review of probability rules',
    },
  },
  'Geometry & Trigonometry': {
    gradient: 'from-amber-500 to-amber-600',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    icon: 'ri-shape-line',
    practiceLink: '/sat-prep/geometry-trig-practice',
    tips: [
      'Memorize the 3-4-5, 5-12-13, and 8-15-17 Pythagorean triples.',
      'For 30-60-90 triangles: sides are x, x√3, 2x. For 45-45-90: x, x, x√2.',
      'SOH-CAH-TOA: write it out before every trig problem.',
      'Complete the square to find circle center and radius from general form.',
    ],
    weeklyGoals: {
      critical: '5 sessions × 40 min — area, trig & coordinate geometry daily',
      'needs-work': '3 sessions × 30 min — focus on trig ratios & special triangles',
      good: '2 sessions × 20 min — mixed geometry practice',
      strong: '1 session × 15 min — coordinate geometry review',
    },
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────
function getPriority(pct: number): 'critical' | 'needs-work' | 'good' | 'strong' {
  if (pct < 50) return 'critical';
  if (pct < 70) return 'needs-work';
  if (pct < 85) return 'good';
  return 'strong';
}

function buildDomainScores(answers: (string | null)[]): DomainScore[] {
  const domains: Domain[] = [
    'Algebra',
    'Advanced Math',
    'Problem-Solving & Data Analysis',
    'Geometry & Trigonometry',
  ];

  return domains.map((domain) => {
    const qs = mockExamQuestions.map((q, i) => ({ q, i })).filter(({ q }) => q.domain === domain);
    const correct = qs.filter(({ q, i }) => answers[i] === q.correctAnswer).length;
    const total = qs.length;
    const pct = Math.round((correct / total) * 100);

    // Subtopic breakdown
    const stMap: Record<string, { correct: number; total: number }> = {};
    qs.forEach(({ q, i }) => {
      if (!stMap[q.subtopic]) stMap[q.subtopic] = { correct: 0, total: 0 };
      stMap[q.subtopic].total++;
      if (answers[i] === q.correctAnswer) stMap[q.subtopic].correct++;
    });
    const subtopics = Object.entries(stMap).map(([name, v]) => ({ name, ...v }));

    return { domain, correct, total, pct, priority: getPriority(pct), subtopics };
  });
}

function buildWeeklySchedule(sorted: DomainScore[]) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const top = sorted[0];
  const second = sorted[1];

  const sessions = [
    { day: days[0], focus: top.domain.split(' ')[0], duration: top.priority === 'critical' ? '45 min' : '30 min', type: 'practice' as const },
    { day: days[1], focus: second.domain.split(' ')[0], duration: second.priority === 'critical' ? '45 min' : '30 min', type: 'practice' as const },
    { day: days[2], focus: top.domain.split(' ')[0], duration: '30 min', type: 'review' as const },
    { day: days[3], focus: sorted[2].domain.split(' ')[0], duration: '30 min', type: 'practice' as const },
    { day: days[4], focus: 'All Domains', duration: '20 min', type: 'review' as const },
    { day: days[5], focus: 'Full Mock Exam', duration: '70 min', type: 'mock' as const },
    { day: days[6], focus: 'Rest Day', duration: '—', type: 'rest' as const },
  ];
  return sessions;
}

// ── Demo answers (used when no real exam data) ─────────────────────────────
const DEMO_ANSWERS: (string | null)[] = [
  'B','A','B','A','D','C','B','C','B','D','A', // Algebra: 7/11 = 64%
  'A','A','A','A','B','A','A','A','D','A','A', // Advanced Math: 9/11 = 82%
  'B','C','C','C','A','C','A','B','C','C','A', // PS&DA: 5/11 = 45%
  'C','B','B','B','C','A','C','C','B','B','B', // Geo&Trig: 6/11 = 55%
];

// ── Main Page ──────────────────────────────────────────────────────────────
export default function StudyPlanPage() {
  const [useDemo, setUseDemo] = useState(true);
  const [customAnswers] = useState<(string | null)[]>(DEMO_ANSWERS);
  const [examScore, setExamScore] = useState<number | null>(null);
  const [examTotal] = useState(44);
  const [activeTab, setActiveTab] = useState<'plan' | 'schedule' | 'tips'>('plan');

  // Allow passing score via URL query param for future integration
  const answers = customAnswers;

  const domainScores = useMemo(() => buildDomainScores(answers), [answers]);
  const sorted = useMemo(
    () => [...domainScores].sort((a, b) => a.pct - b.pct),
    [domainScores]
  );
  const weeklySchedule = useMemo(() => buildWeeklySchedule(sorted), [sorted]);

  const overallCorrect = answers.filter((a, i) => a === mockExamQuestions[i]?.correctAnswer).length;
  const overallPct = Math.round((overallCorrect / examTotal) * 100);

  const criticalCount = sorted.filter((d) => d.priority === 'critical').length;
  const needsWorkCount = sorted.filter((d) => d.priority === 'needs-work').length;
  const strongCount = sorted.filter((d) => d.priority === 'strong').length;

  const totalWeeklyHours = sorted.reduce((acc, d) => {
    const mins = { critical: 225, 'needs-work': 90, good: 40, strong: 15 }[d.priority];
    return acc + mins;
  }, 0);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          /* Hide non-essential elements */
          .no-print,
          button:not(.print-keep),
          nav,
          .print-hide {
            display: none !important;
          }

          /* Reset page styles */
          body {
            background: white !important;
            margin: 0;
            padding: 0;
          }

          /* Page setup */
          @page {
            size: A4;
            margin: 1.5cm;
          }

          /* Remove backgrounds and shadows for print */
          * {
            box-shadow: none !important;
            background-image: none !important;
          }

          /* Convert gradients to solid colors */
          .print-header {
            background: #10b981 !important;
            color: white !important;
            padding: 20px !important;
            margin-bottom: 20px !important;
            border-radius: 8px !important;
          }

          /* Ensure readable text */
          .print-content {
            color: #000 !important;
            font-size: 10pt !important;
            line-height: 1.4 !important;
          }

          /* Domain cards */
          .print-domain-card {
            page-break-inside: avoid;
            border: 1px solid #ddd !important;
            background: white !important;
            padding: 12px !important;
            margin-bottom: 12px !important;
            border-radius: 6px !important;
          }

          /* Tables and schedules */
          .print-schedule {
            page-break-inside: avoid;
            border: 1px solid #ddd !important;
            background: white !important;
            padding: 12px !important;
            margin-bottom: 12px !important;
          }

          /* Progress bars - convert to simple borders */
          .print-progress-bar {
            border: 1px solid #333 !important;
            background: white !important;
            height: 8px !important;
          }

          .print-progress-fill {
            background: #333 !important;
          }

          /* Icons - ensure visibility */
          i {
            color: #333 !important;
          }

          /* Headings */
          h1, h2, h3, h4 {
            color: #000 !important;
            page-break-after: avoid;
          }

          /* Lists */
          ul, ol {
            page-break-inside: avoid;
          }

          /* Grid layouts - convert to single column for print */
          .print-grid {
            display: block !important;
          }

          .print-grid > * {
            margin-bottom: 12px !important;
          }

          /* Remove hover effects */
          a {
            color: #000 !important;
            text-decoration: none !important;
          }

          /* Ensure all tabs content is visible */
          .print-all-tabs {
            display: block !important;
          }

          /* Page breaks */
          .print-page-break {
            page-break-before: always;
          }

          /* Score summary box */
          .print-score-box {
            border: 2px solid #333 !important;
            background: #f5f5f5 !important;
            padding: 15px !important;
            margin-bottom: 20px !important;
            text-align: center !important;
          }

          /* Tips and checklists */
          .print-tip-box {
            border-left: 3px solid #333 !important;
            padding-left: 10px !important;
            margin-bottom: 8px !important;
          }

          /* Roadmap boxes */
          .print-roadmap-item {
            border: 1px solid #ddd !important;
            background: white !important;
            padding: 10px !important;
            margin-bottom: 10px !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50">
        {/* ── Hero ── */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-14 print-header">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-3 no-print">
                  <Link
                    to="/sat-prep"
                    className="flex items-center gap-1.5 text-emerald-200 hover:text-white text-sm font-semibold transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line" />
                    SAT Prep
                  </Link>
                  <span className="text-emerald-300">/</span>
                  <span className="text-emerald-100 text-sm">Study Plan</span>
                </div>
                <h1 className="text-4xl font-black mb-2">Personalized Study Plan</h1>
                <p className="text-emerald-100 text-lg max-w-xl">
                  Based on your mock exam performance, here's exactly what to focus on to maximize your SAT Math score.
                </p>
              </div>

              {/* Overall score pill */}
              <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 text-center min-w-[180px] border border-white/20 print-score-box">
                <p className="text-emerald-200 text-xs font-bold uppercase tracking-wider mb-1">Overall Score</p>
                <div className="text-6xl font-black mb-1">{overallPct}%</div>
                <p className="text-emerald-100 text-sm">{overallCorrect}/{examTotal} correct</p>
                <div className="mt-3 bg-white/20 rounded-full h-2 overflow-hidden print-progress-bar">
                  <div className="h-full bg-white rounded-full print-progress-fill" style={{ width: `${overallPct}%` }} />
                </div>
              </div>
            </div>

            {/* Print button */}
            <div className="mt-6 no-print">
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-bold px-6 py-3 rounded-xl hover:opacity-90 transition-opacity whitespace-nowrap border border-white/30"
              >
                <i className="ri-printer-line" />
                Print Study Plan
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 print-content">

          {/* ── Demo notice ── */}
          {useDemo && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-8 flex items-start gap-3 no-print">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-amber-600 text-xl" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-800 mb-0.5">Showing a sample study plan</p>
                <p className="text-xs text-amber-700">
                  Take the{' '}
                  <Link to="/sat-prep/mock-exam" className="underline font-semibold hover:text-amber-900">
                    Full SAT Math Mock Exam
                  </Link>{' '}
                  first, then return here to see your personalized plan based on your actual results.
                </p>
              </div>
              <Link
                to="/sat-prep/mock-exam"
                className="flex-shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
              >
                Take Exam Now
              </Link>
            </div>
          )}

          {/* ── Summary stats ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 print-grid">
            {[
              {
                label: 'Critical Focus',
                value: criticalCount,
                icon: 'ri-alarm-warning-fill',
                color: 'text-red-600',
                bg: 'bg-red-50',
                border: 'border-red-200',
              },
              {
                label: 'Needs Work',
                value: needsWorkCount,
                icon: 'ri-error-warning-fill',
                color: 'text-amber-600',
                bg: 'bg-amber-50',
                border: 'border-amber-200',
              },
              {
                label: 'Strong Domains',
                value: strongCount,
                icon: 'ri-medal-fill',
                color: 'text-emerald-600',
                bg: 'bg-emerald-50',
                border: 'border-emerald-200',
              },
              {
                label: 'Weekly Study Time',
                value: `${Math.round(totalWeeklyHours / 60)}h ${totalWeeklyHours % 60}m`,
                icon: 'ri-time-line',
                color: 'text-teal-600',
                bg: 'bg-teal-50',
                border: 'border-teal-200',
              },
            ].map((s) => (
              <div key={s.label} className={`${s.bg} border ${s.border} rounded-2xl p-5 text-center print-domain-card`}>
                <div className={`w-10 h-10 flex items-center justify-center mx-auto mb-2 ${s.color}`}>
                  <i className={`${s.icon} text-2xl`} />
                </div>
                <div className={`text-3xl font-black ${s.color}`}>{s.value}</div>
                <p className="text-xs text-gray-500 font-medium mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          {/* ── Tabs ── */}
          <div className="flex bg-white rounded-full p-1.5 shadow-md mb-8 w-fit no-print">
            {[
              { id: 'plan', label: 'Domain Priority Plan', icon: 'ri-list-check-2' },
              { id: 'schedule', label: 'Weekly Schedule', icon: 'ri-calendar-2-line' },
              { id: 'tips', label: 'Test-Day Tips', icon: 'ri-lightbulb-line' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className={tab.icon} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Print: Show all tabs */}
          <div className="hidden print:block print-all-tabs">
            {/* Domain Priority Plan */}
            <div className="mb-8">
              <h2 className="text-2xl font-black text-gray-900 mb-4">Domain Priority Plan</h2>
              <p className="text-gray-600 text-sm mb-6">
                Ranked from most urgent to strongest — start at #1 and work your way down.
              </p>

              <div className="space-y-4 print-grid">
                {sorted.map((ds, idx) => {
                  const meta = DOMAIN_META[ds.domain];
                  return (
                    <div key={ds.domain} className="print-domain-card">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center text-gray-700 font-black text-sm">
                          #{idx + 1}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-black text-gray-900">{ds.domain}</h3>
                          <p className="text-sm text-gray-600">
                            Score: {ds.correct}/{ds.total} ({ds.pct}%)
                          </p>
                        </div>
                      </div>
                      <div className="mb-3">
                        <p className="text-xs font-bold text-gray-700 mb-1">Weekly Goal:</p>
                        <p className="text-xs text-gray-600">{meta.weeklyGoals[ds.priority]}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-700 mb-2">Key Tips:</p>
                        <ul className="space-y-1">
                          {meta.tips.map((tip, i) => (
                            <li key={i} className="text-xs text-gray-600 print-tip-box">
                              • {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {ds.subtopics.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-bold text-gray-700 mb-2">Subtopic Breakdown:</p>
                          <div className="space-y-1">
                            {ds.subtopics.map((st) => {
                              const stPct = Math.round((st.correct / st.total) * 100);
                              return (
                                <div key={st.name} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-600">{st.name}</span>
                                  <span className="font-semibold text-gray-800">
                                    {st.correct}/{st.total} ({stPct}%)
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Weekly Schedule */}
            <div className="mb-8 print-page-break">
              <h2 className="text-2xl font-black text-gray-900 mb-4">Weekly Schedule</h2>
              <div className="print-schedule">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2 font-bold">Day</th>
                      <th className="text-left py-2 font-bold">Focus</th>
                      <th className="text-left py-2 font-bold">Duration</th>
                      <th className="text-left py-2 font-bold">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklySchedule.map((session) => (
                      <tr key={session.day} className="border-b border-gray-200">
                        <td className="py-2 font-semibold">{session.day}</td>
                        <td className="py-2">{session.focus}</td>
                        <td className="py-2">{session.duration}</td>
                        <td className="py-2 capitalize">{session.type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Study time breakdown */}
              <div className="mt-6 print-schedule">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Weekly Time Per Domain</h3>
                <div className="space-y-2">
                  {sorted.map((ds) => {
                    const mins = { critical: 225, 'needs-work': 90, good: 40, strong: 15 }[ds.priority];
                    return (
                      <div key={ds.domain} className="flex items-center justify-between text-xs py-1 border-b border-gray-200">
                        <span className="font-semibold text-gray-800">{ds.domain}</span>
                        <span className="text-gray-600">
                          {mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60 > 0 ? `${mins % 60}m` : ''}` : `${mins}m`}
                        </span>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between text-sm py-2 font-bold border-t-2 border-gray-400">
                    <span>Total Weekly Study Time</span>
                    <span>{Math.floor(totalWeeklyHours / 60)}h {totalWeeklyHours % 60}m</span>
                  </div>
                </div>
              </div>

              {/* 4-week roadmap */}
              <div className="mt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">4-Week Improvement Roadmap</h3>
                <div className="space-y-3">
                  {[
                    {
                      week: 'Week 1',
                      title: 'Foundation',
                      focus: `Deep dive into ${sorted[0].domain} — your biggest gap.`,
                      goal: `Reach 60%+ on ${sorted[0].domain} practice test`,
                    },
                    {
                      week: 'Week 2',
                      title: 'Build Up',
                      focus: `Attack ${sorted[1].domain} while reviewing Week 1 material.`,
                      goal: `Reach 65%+ on ${sorted[1].domain} practice test`,
                    },
                    {
                      week: 'Week 3',
                      title: 'Consolidate',
                      focus: 'Mixed practice across all 4 domains. Timed sets.',
                      goal: 'Score 70%+ on a full timed mock exam',
                    },
                    {
                      week: 'Week 4',
                      title: 'Peak Performance',
                      focus: 'Two full mock exams. Review errors only. Rest before test day.',
                      goal: 'Score 80%+ on final mock exam',
                    },
                  ].map((w) => (
                    <div key={w.week} className="print-roadmap-item">
                      <p className="font-bold text-sm mb-1">
                        {w.week}: {w.title}
                      </p>
                      <p className="text-xs text-gray-600 mb-1">{w.focus}</p>
                      <p className="text-xs text-gray-800">
                        <strong>Goal:</strong> {w.goal}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Test-Day Tips */}
            <div className="print-page-break">
              <h2 className="text-2xl font-black text-gray-900 mb-4">Test-Day Tips</h2>
              
              {/* General strategies */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">General SAT Math Strategies</h3>
                <div className="space-y-2">
                  {[
                    { title: 'Pace Yourself', tip: 'Spend ~1.5 min per question. If stuck after 90 seconds, skip and return.' },
                    { title: 'Use Your Calculator Wisely', tip: 'Estimate first — if your answer is wildly off, recheck your setup.' },
                    { title: 'Read Every Word', tip: 'Words like "integer," "positive," and "at least" change the answer entirely.' },
                    { title: 'Plug In Numbers', tip: 'For abstract algebra, substitute simple numbers (1, 2, 0) to test choices.' },
                    { title: 'Eliminate Wrong Answers', tip: 'Cross out clearly wrong choices first to improve your odds.' },
                    { title: 'Check Units & Labels', tip: 'Data questions often hide answers in axis labels or table headers.' },
                  ].map((s) => (
                    <div key={s.title} className="print-tip-box">
                      <p className="text-xs font-bold text-gray-800">{s.title}</p>
                      <p className="text-xs text-gray-600">{s.tip}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Domain-specific tips */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Domain-Specific Tips</h3>
                {sorted.map((ds) => {
                  const meta = DOMAIN_META[ds.domain];
                  return (
                    <div key={ds.domain} className="mb-4 print-domain-card">
                      <h4 className="font-bold text-sm mb-2">{ds.domain}</h4>
                      <ul className="space-y-1">
                        {meta.tips.map((tip, i) => (
                          <li key={i} className="text-xs text-gray-600">
                            • {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              {/* Test-day checklist */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Test-Day Checklist</h3>
                <div className="space-y-3">
                  {[
                    {
                      title: 'Night Before',
                      items: [
                        'Review your weak subtopics one last time',
                        'Prepare your calculator with fresh batteries',
                        'Pack your admission ticket and ID',
                        'Get 8+ hours of sleep — no late cramming',
                      ],
                    },
                    {
                      title: 'Morning Of',
                      items: [
                        'Eat a protein-rich breakfast',
                        'Arrive 30 minutes early',
                        'Bring water and a snack for the break',
                        'Do a 5-minute mental warm-up with easy problems',
                      ],
                    },
                    {
                      title: 'During the Exam',
                      items: [
                        'Read each question fully before answering',
                        'Skip and flag hard questions — return later',
                        'Use all remaining time to check your work',
                        'Stay calm — one hard question won\'t sink your score',
                      ],
                    },
                  ].map((section) => (
                    <div key={section.title} className="print-roadmap-item">
                      <h4 className="font-bold text-sm mb-2">{section.title}</h4>
                      <ul className="space-y-1">
                        {section.items.map((item, i) => (
                          <li key={i} className="text-xs text-gray-600">
                            ☐ {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Screen: Show active tab only */}
          <div className="print:hidden">
            {/* ── Tab: Domain Priority Plan ── */}
            {activeTab === 'plan' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900">Your Domain Priority Order</h2>
                    <p className="text-gray-500 text-sm mt-1">
                      Ranked from most urgent to strongest — start at #1 and work your way down.
                    </p>
                  </div>
                  <div className="hidden md:flex items-center gap-3">
                    {[
                      { label: 'Critical', color: 'bg-red-500' },
                      { label: 'Needs Work', color: 'bg-amber-500' },
                      { label: 'On Track', color: 'bg-teal-500' },
                      { label: 'Strong', color: 'bg-emerald-500' },
                    ].map((l) => (
                      <div key={l.label} className="flex items-center gap-1.5">
                        <div className={`w-2.5 h-2.5 rounded-full ${l.color}`} />
                        <span className="text-xs text-gray-500">{l.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {sorted.map((ds, idx) => {
                    const meta = DOMAIN_META[ds.domain];
                    return (
                      <DomainPriorityCard
                        key={ds.domain}
                        rank={idx + 1}
                        domain={ds.domain}
                        score={ds.correct}
                        total={ds.total}
                        priority={ds.priority}
                        practiceLink={meta.practiceLink}
                        subtopics={ds.subtopics}
                        color={{ gradient: meta.gradient, bg: meta.bg, text: meta.text, border: meta.border, badge: meta.badge }}
                        icon={meta.icon}
                        weeklyGoal={meta.weeklyGoals[ds.priority]}
                        tips={meta.tips}
                      />
                    );
                  })}
                </div>

                {/* Action row */}
                <div className="mt-8 bg-white rounded-2xl shadow-lg p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="font-black text-gray-900 text-lg">Ready to start practicing?</h3>
                    <p className="text-gray-500 text-sm mt-1">
                      Jump straight into your #1 priority domain — {sorted[0].domain}.
                    </p>
                  </div>
                  <div className="flex gap-3 flex-shrink-0">
                    <Link
                      to={DOMAIN_META[sorted[0].domain].practiceLink}
                      className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold px-6 py-3 rounded-xl hover:opacity-90 transition-opacity whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line" />
                      Start Priority #1
                    </Link>
                    <Link
                      to="/sat-prep/mock-exam"
                      className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-6 py-3 rounded-xl transition-colors whitespace-nowrap"
                    >
                      <i className="ri-timer-line" />
                      Retake Mock Exam
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* ── Tab: Weekly Schedule ── */}
            {activeTab === 'schedule' && (
              <div className="space-y-6">
                <WeeklySchedule sessions={weeklySchedule} />

                {/* Study time breakdown */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-pie-chart-line text-emerald-600 text-xl" />
                    </div>
                    Recommended Weekly Time Per Domain
                  </h3>
                  <div className="space-y-4">
                    {sorted.map((ds) => {
                      const mins = { critical: 225, 'needs-work': 90, good: 40, strong: 15 }[ds.priority];
                      const pctOfTotal = Math.round((mins / totalWeeklyHours) * 100);
                      const meta = DOMAIN_META[ds.domain];
                      return (
                        <div key={ds.domain}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <div className={`w-6 h-6 flex items-center justify-center`}>
                                <i className={`${meta.icon} ${meta.text} text-base`} />
                              </div>
                              <span className="text-sm font-semibold text-gray-800">{ds.domain}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-500">{pctOfTotal}% of study time</span>
                              <span className={`text-sm font-bold ${meta.text}`}>
                                {mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60 > 0 ? `${mins % 60}m` : ''}` : `${mins}m`}
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${meta.gradient} transition-all duration-700`}
                              style={{ width: `${pctOfTotal}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-700">Total Weekly Study Time</span>
                    <span className="text-lg font-black text-emerald-600">
                      {Math.floor(totalWeeklyHours / 60)}h {totalWeeklyHours % 60}m
                    </span>
                  </div>
                </div>

                {/* 4-week roadmap */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-road-map-line text-emerald-600 text-xl" />
                    </div>
                    4-Week Improvement Roadmap
                  </h3>
                  <div className="grid md:grid-cols-4 gap-4">
                    {[
                      {
                        week: 'Week 1',
                        title: 'Foundation',
                        focus: `Deep dive into ${sorted[0].domain} — your biggest gap.`,
                        goal: `Reach 60%+ on ${sorted[0].domain} practice test`,
                        color: 'bg-red-50 border-red-200',
                        textColor: 'text-red-700',
                        icon: 'ri-foundation-line',
                      },
                      {
                        week: 'Week 2',
                        title: 'Build Up',
                        focus: `Attack ${sorted[1].domain} while reviewing Week 1 material.`,
                        goal: `Reach 65%+ on ${sorted[1].domain} practice test`,
                        color: 'bg-amber-50 border-amber-200',
                        textColor: 'text-amber-700',
                        icon: 'ri-building-line',
                      },
                      {
                        week: 'Week 3',
                        title: 'Consolidate',
                        focus: 'Mixed practice across all 4 domains. Timed sets.',
                        goal: 'Score 70%+ on a full timed mock exam',
                        color: 'bg-teal-50 border-teal-200',
                        textColor: 'text-teal-700',
                        icon: 'ri-stack-line',
                      },
                      {
                        week: 'Week 4',
                        title: 'Peak Performance',
                        focus: 'Two full mock exams. Review errors only. Rest before test day.',
                        goal: 'Score 80%+ on final mock exam',
                        color: 'bg-emerald-50 border-emerald-200',
                        textColor: 'text-emerald-700',
                        icon: 'ri-trophy-line',
                      },
                    ].map((w) => (
                      <div key={w.week} className={`${w.color} border rounded-xl p-4`}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-7 h-7 flex items-center justify-center ${w.textColor}`}>
                            <i className={`${w.icon} text-lg`} />
                          </div>
                          <div>
                            <p className={`text-xs font-black uppercase tracking-wider ${w.textColor}`}>{w.week}</p>
                            <p className="text-sm font-bold text-gray-800">{w.title}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mb-3 leading-relaxed">{w.focus}</p>
                        <div className="bg-white/70 rounded-lg p-2">
                          <p className="text-xs font-bold text-gray-500 mb-0.5">Goal</p>
                          <p className={`text-xs font-semibold ${w.textColor}`}>{w.goal}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Tab: Test-Day Tips ── */}
            {activeTab === 'tips' && (
              <div className="space-y-6">
                {/* General tips */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-star-line text-amber-500 text-xl" />
                    </div>
                    General SAT Math Strategies
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-time-line', title: 'Pace Yourself', tip: 'Spend ~1.5 min per question. If stuck after 90 seconds, skip and return — never let one question eat your time.' },
                      { icon: 'ri-calculator-line', title: 'Use Your Calculator Wisely', tip: 'The calculator is a tool, not a crutch. Estimate first — if your answer is wildly off, recheck your setup.' },
                      { icon: 'ri-eye-line', title: 'Read Every Word', tip: 'SAT questions are precise. Words like "integer," "positive," and "at least" change the answer entirely.' },
                      { icon: 'ri-arrow-go-back-line', title: 'Plug In Numbers', tip: 'For abstract algebra questions, substitute simple numbers (1, 2, 0) to test answer choices quickly.' },
                      { icon: 'ri-checkbox-multiple-line', title: 'Eliminate Wrong Answers', tip: 'Cross out clearly wrong choices first. Even eliminating 1–2 options dramatically improves your odds.' },
                      { icon: 'ri-check-double-line', title: 'Check Units & Labels', tip: 'Data analysis questions often hide the answer in the axis labels or table headers. Always re-read them.' },
                    ].map((s) => (
                      <div key={s.title} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className={`${s.icon} text-emerald-600 text-lg`} />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-sm mb-1">{s.title}</p>
                          <p className="text-xs text-gray-600 leading-relaxed">{s.tip}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Domain-specific tips */}
                <div className="grid md:grid-cols-2 gap-6">
                  {sorted.map((ds) => {
                    const meta = DOMAIN_META[ds.domain];
                    return (
                      <div key={ds.domain} className={`${meta.bg} border ${meta.border} rounded-2xl p-5`}>
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-10 h-10 bg-gradient-to-br ${meta.gradient} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>
                            <i className={`${meta.icon} text-lg`} />
                          </div>
                          <div>
                            <h4 className="font-black text-gray-900 text-sm">{ds.domain}</h4>
                            <span className={`text-xs font-bold ${meta.text}`}>{ds.pct}% on mock exam</span>
                          </div>
                        </div>
                        <ul className="space-y-2">
                          {meta.tips.map((tip, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                              <i className="ri-lightbulb-flash-line text-amber-500 mt-0.5 flex-shrink-0" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>

                {/* Test-day checklist */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-emerald-600 text-xl" />
                    </div>
                    Test-Day Checklist
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {[
                      {
                        title: 'Night Before',
                        color: 'bg-teal-50 border-teal-200',
                        textColor: 'text-teal-700',
                        items: [
                          'Review your weak subtopics one last time',
                          'Prepare your calculator with fresh batteries',
                          'Pack your admission ticket and ID',
                          'Get 8+ hours of sleep — no late cramming',
                        ],
                      },
                      {
                        title: 'Morning Of',
                        color: 'bg-emerald-50 border-emerald-200',
                        textColor: 'text-emerald-700',
                        items: [
                          'Eat a protein-rich breakfast',
                          'Arrive 30 minutes early',
                          'Bring water and a snack for the break',
                          'Do a 5-minute mental warm-up with easy problems',
                        ],
                      },
                      {
                        title: 'During the Exam',
                        color: 'bg-amber-50 border-amber-200',
                        textColor: 'text-amber-700',
                        items: [
                          'Read each question fully before answering',
                          'Skip and flag hard questions — return later',
                          'Use all remaining time to check your work',
                          'Stay calm — one hard question won\'t sink your score',
                        ],
                      },
                    ].map((section) => (
                      <div key={section.title} className={`${section.color} border rounded-xl p-4`}>
                        <h4 className={`font-black text-sm mb-3 ${section.textColor}`}>{section.title}</h4>
                        <ul className="space-y-2">
                          {section.items.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                              <i className="ri-checkbox-blank-circle-line text-gray-400 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Bottom CTA ── */}
          <div className="mt-10 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6 no-print">
            <div>
              <h3 className="text-2xl font-black mb-1">Track Your Progress</h3>
              <p className="text-emerald-100 text-sm max-w-md">
                Retake the mock exam after completing your study plan to see how much you've improved across all 4 domains.
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Link
                to="/sat-prep/mock-exam"
                className="flex items-center gap-2 bg-white text-emerald-700 font-black px-6 py-3 rounded-xl hover:bg-emerald-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-timer-flash-line" />
                Take Mock Exam
              </Link>
              <Link
                to="/sat-prep"
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-bold px-6 py-3 rounded-xl transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                Back to SAT Prep
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
